import {
  Button,
  Col,
  DatePicker,
  Form,
  Icon,
  Input,
  Layout,
  Menu,
  Row,
  Select,
  Table,
  Tabs,
  Tooltip,
  Typography,
} from 'antd';
import { FormComponentProps } from 'antd/lib/form';
import { ColumnProps } from 'antd/lib/table';
import { isArray, isEmpty, size } from 'lodash';
import moment from 'moment';
import { InfoList, LongTextTd, O2OCustomPaging } from 'o2o_layout';
import React, { useEffect, useState } from 'react';
import { useAsyncRetry } from 'react-use';
import CommonTypeProps, { ICommonPayload } from '../../../common/CommonTypeProps';
import DrawerViewJSON from '../../../shares/DrawerViewJSON';
import { TagPrimaryColor } from '../../../shares/TagPrimaryColor';
import { TypeCriteriaExportsHistory } from '../exports-history/DAL';
import { LoginActivityDAL } from './DAL';
import { LoginActivities, TypeLoginActivity } from './model';
import { O2OFormItem } from '../../../shares/O2OFormItem';
import DashboardPageLayoutV2 from '../../../layout/dashboard/DashboardPageLayoutV2';
import { LinkO2O } from '../../../common/LinkO2O';
import { FilterSortData } from '@shares/FilterSortData';

interface ILoginActivityProps extends CommonTypeProps {}

const TYPE_DISPATCH_CRITERIA = 'TYPE_DISPATCH_CRITERIA';

const initCriteria: ICommonPayload<TypeCriteriaExportsHistory> = {
  loading: false,
  type: TYPE_DISPATCH_CRITERIA,
  payload: {} as TypeCriteriaExportsHistory,
};

const reducerCriteria = (
  state: ICommonPayload<TypeCriteriaExportsHistory>,
  action: ICommonPayload<TypeCriteriaExportsHistory>
) => {
  switch (action.type) {
    case TYPE_DISPATCH_CRITERIA:
      return { ...state, ...action };
    default:
      throw new Error();
  }
};

const LoginActivityComponentV2 = (props: ILoginActivityProps) => {
  const [criteria, dispatchCriteria] = React.useReducer(reducerCriteria, initCriteria);

  const [selectedRecord, setSelectedRecord] = useState(undefined as undefined | TypeLoginActivity);
  const [visibleViewJSON, setVisibleViewJSON] = useState(false);

  useEffect(() => {
    dispatchCriteria({
      loading: false,
      type: TYPE_DISPATCH_CRITERIA,
      payload: {
        ...{ page: 0, pageSize: 10, orderBy: 'actionAt:desc' },
        ...criteria.payload,
      } as TypeCriteriaExportsHistory,
    });
    return () => {
      dispatchCriteria({
        loading: false,
        type: TYPE_DISPATCH_CRITERIA,
        payload: {} as TypeCriteriaExportsHistory,
      });
    };
  }, []);

  const load = async () => {
    const result = props.commonActionFromDALV2(LoginActivityDAL.search, criteria.payload);
    try {
      const response = await result;
      return response;
    } catch (error) {
      return new LoginActivities();
    }
  };

  const stateLoginActivity = useAsyncRetry(async () => {
    if (!isEmpty(criteria.payload)) {
      return await load();
    }
    return new LoginActivities();
  }, [criteria]);

  const loginActivity = stateLoginActivity.value || new LoginActivities();

  const columns: ColumnProps<TypeLoginActivity>[] = [
    {
      title: '#',
      key: 'index',
      sorter: false,
      width: 40,
      align: 'center',
      render: (text: any, record: TypeLoginActivity, index: number) => {
        return index + 1;
      },
    },
    {
      title: 'Email',
      key: 'email',
      sorter: false,
      width: 160,
      ellipsis: true,
      render: (text: any, record: TypeLoginActivity, index: number) => {
        return <LongTextTd text={record.userId} />;
      },
    },
    {
      title: 'Action at',
      key: 'action_at',
      width: 120,
      ellipsis: true,
      render(text: any, record: TypeLoginActivity, index: number) {
        return record.actionAt ? (
          <Tooltip
            title={
              <>
                Action at {moment(record.actionAt).format('LLLL')} by {record.actionBy}{' '}
              </>
            }
          >
            {moment(record.actionAt).fromNow()}
          </Tooltip>
        ) : (
          '--/--'
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      ellipsis: true,
      render(text: any, record: TypeLoginActivity, index: number) {
        return <TagPrimaryColor children={record.status} uiSetting={props.uiSetting} />;
      },
    },
    {
      title: 'Source',
      key: 'source',
      ellipsis: true,
      width: 100,
      render: (text: any, record: TypeLoginActivity, index: number) => {
        return <TagPrimaryColor children={record.source} uiSetting={props.uiSetting} />;
      },
    },
    {
      title: 'Device',
      key: 'device',
      sorter: false,
      width: 150,
      render: (text: any, record: TypeLoginActivity, index: number) => {
        return <LongTextTd text={record.deviceCtx} />;
      },
    },
    {
      title: 'OS Name',
      key: 'os_name',
      sorter: false,
      width: 100,
      ellipsis: true,
      render: (text: any, record: TypeLoginActivity, index: number) => {
        return <LongTextTd text={record.osNameCtx} />;
      },
    },
    {
      title: 'Browser',
      key: 'browser',
      sorter: false,
      width: 100,
      ellipsis: true,
      render: (text: any, record: TypeLoginActivity, index: number) => {
        return <LongTextTd text={record.browserNameCtx} />;
      },
    },
    {
      title: 'IP Adress',
      key: 'ip',
      sorter: false,
      width: 150,
      ellipsis: true,
      render: (text: any, record: TypeLoginActivity, index: number) => {
        return <LongTextTd text={record.ipAddress} />;
      },
    },
    {
      title: 'Message',
      key: 'message',
      sorter: false,
      ellipsis: true,
      render: (text: any, record: TypeLoginActivity, index: number) => {
        return <LongTextTd text={record.extraValue} />;
      },
    },
    {
      title: 'Action',
      key: 'action',
      width: 70,
      fixed: 'right',
      render(text: any, record: TypeLoginActivity, index: number) {
        return (
          <>
            <Button
              size='small'
              icon='select'
              type='link'
              onClick={() => {
                setSelectedRecord(record);
                setVisibleViewJSON(true);
              }}
            />
          </>
        );
      },
    },
  ];

  const renderContent = () => {
    return (
      <Row type='flex' gutter={[10, 10]}>
        <Col xs={24}>
          <Typography.Title level={4}>Login Activity</Typography.Title>
          <FilterLoginActivityComponent
            loading={stateLoginActivity.loading}
            onSearch={(payload: any) => {
              dispatchCriteria({
                loading: false,
                payload: { ...criteria.payload, ...(payload as TypeCriteriaExportsHistory) },
                type: TYPE_DISPATCH_CRITERIA,
              });
            }}
          />
        </Col>
        <Col
          xs={24}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <InfoList loading={stateLoginActivity.loading} list={loginActivity} refresh={stateLoginActivity.retry} />
          <FilterSortData
            items={[
              {
                key: 'actionAt',
                name: 'Action Time',
              },
            ]}
            isLoading={stateLoginActivity.loading}
            orderBy={criteria.payload.orderBy}
            onChangeOrderBy={(sort: any) => {
              dispatchCriteria({
                loading: false,
                type: TYPE_DISPATCH_CRITERIA,
                payload: { ...criteria.payload, ...{ orderBy: sort } } as any,
              });
            }}
          />
        </Col>
        <Col xs={24}>
          <Table
            loading={stateLoginActivity.loading}
            columns={columns}
            dataSource={loginActivity.results.content}
            rowKey='id'
            size='small'
            scroll={{ x: 'calc(700px + 50%)' }}
            pagination={false}
          />
        </Col>
        <Col xs={24}>
          <O2OCustomPaging
            list={loginActivity}
            onChange={(e: number) => {
              dispatchCriteria({
                loading: false,
                payload: { ...criteria.payload, ...({ page: e - 1 } as TypeCriteriaExportsHistory) },
                type: TYPE_DISPATCH_CRITERIA,
              });
            }}
            onChangePageSize={(e: number) => {
              dispatchCriteria({
                loading: false,
                payload: { ...criteria.payload, ...({ page: 0, pageSize: e } as TypeCriteriaExportsHistory) },
                type: TYPE_DISPATCH_CRITERIA,
              });
            }}
          />
        </Col>

        <DrawerViewJSON
          data={selectedRecord}
          onClose={() => {
            setSelectedRecord(undefined);
            setVisibleViewJSON(false);
          }}
          visible={visibleViewJSON}
        />
      </Row>
    );
  };

  const renderContentLayout = (version?: string) => {
    return version === 'v2' ? (
      <>
        <Tabs
          defaultActiveKey='activity'
          onChange={(key: string) => {
            if (key === 'my-profile') props.history.push({ pathname: '/my-account-info' });
            if (key === 'security') props.history.push({ pathname: '/security/2fa-security' });
            if (key === 'activity') props.history.push({ pathname: '/activity/invitation' });
          }}
        >
          <Tabs.TabPane tab='My Profile' key='my-profile' />
          <Tabs.TabPane tab='Security' key='security' />
          <Tabs.TabPane tab='Activity' key='activity' />
        </Tabs>
        <Layout>
          <Layout.Sider
            width={200}
            style={{ background: '#fff' }}
            className={version === 'v2' ? 'main-menu-v3 menu-connection' : ''}
          >
            <Menu mode='inline' defaultSelectedKeys={['login-history']} style={{ height: '100%' }}>
              <Menu.Item key='invitation'>
                <LinkO2O {...props} to={{ pathname: '/activity/invitation' }}>
                  Invitation
                </LinkO2O>
              </Menu.Item>
              <Menu.Item key='export-history'>
                <LinkO2O {...props} to={{ pathname: '/activity/exports-history' }}>
                  Export History
                </LinkO2O>
              </Menu.Item>
              <Menu.Item key='login-history'>
                <LinkO2O {...props} to={{ pathname: '/activity/login-history' }}>
                  Login Activity
                </LinkO2O>
              </Menu.Item>
            </Menu>
          </Layout.Sider>
          <Layout.Content style={{ overflow: 'hidden' }} className='pl30 pt10'>
            {renderContent()}
          </Layout.Content>
        </Layout>
      </>
    ) : (
      renderContent()
    );
  };

  return (
    <DashboardPageLayoutV2
      props={props}
      selectedLeftNavStandard='login-history'
      selectedSubMenuStandard='activity'
      selectedLeftNavModern='my-info'
      selectedSubMenuModern='my-info'
      breadcrumbItemsStandard={[
        {
          title: 'Home',
          path: '/',
        },
        {
          title: 'Activity',
        },
        {
          title: 'Login Activity',
        },
      ]}
      breadcrumbItemsModern={[
        { title: 'Account', path: '/' },
        { title: 'Profile', path: '/activity/invitation' },
        { title: 'Activity' },
      ]}
      pageTitle='Profile'
      renderContentLayout={renderContentLayout}
      metaTitle={props.ui.meta.title}
      pageTitleIcon={
        <Icon
          component={() => (
            <svg width='16' height='17' viewBox='0 0 16 17' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <path
                d='M2.46644 16.3333H13.5335C14.8691 16.3333 15.5 15.9188 15.5 15.0228C15.5 12.7695 12.6476 9.53332 7.99663 9.53332C3.35234 9.53332 0.5 12.7695 0.5 15.0228C0.5 15.9188 1.13087 16.3333 2.46644 16.3333ZM2.13758 15.2167C1.80873 15.2167 1.68792 15.1298 1.68792 14.8891C1.68792 13.3311 3.96308 10.6566 7.99663 10.6566C12.0369 10.6566 14.3121 13.3311 14.3121 14.8891C14.3121 15.1298 14.1913 15.2167 13.8624 15.2167H2.13758ZM8.01003 8.39661C10.0436 8.39661 11.6812 6.61134 11.6812 4.39816C11.6812 2.22508 10.0436 0.5 8.01003 0.5C5.98322 0.5 4.33221 2.25183 4.33221 4.41153C4.33221 6.61803 5.9765 8.39661 8.01003 8.39661ZM8.01003 7.27998C6.64764 7.27998 5.52013 6.02295 5.52013 4.41153C5.52013 2.84691 6.64093 1.61662 8.01003 1.61662C9.37917 1.61662 10.4933 2.82686 10.4933 4.39816C10.4933 6.00958 9.3725 7.27998 8.01003 7.27998Z'
                fill='currentColor'
              />
            </svg>
          )}
        />
      }
    />
  );
};

export default LoginActivityComponentV2;

interface IFilterProps extends FormComponentProps {
  onSearch: (payload: any) => void;
  loading: boolean;
}

const FilterLoginActivityComponent = Form.create<IFilterProps>()((props: IFilterProps) => {
  const onSubmit = () => {
    props.form.validateFields((err, values: any) => {
      if (!err) {
        if (isArray(values.time) && size(values.time) === 2) {
          values['fromTime'] = moment(values.time[0]).toDate().getTime();
          values['toTime'] = moment(values.time[1]).toDate().getTime();
        } else {
          values['fromTime'] = 0;
          values['toTime'] = 0;
        }
        delete values.time;
        props.onSearch(values);
      }
    });
  };

  return (
    <Form hideRequiredMark colon={false}>
      <Row type='flex' gutter={[5, 5]}>
        <Col xs={24} lg={12} xl={8} xxl={6}>
          <O2OFormItem label='Info'>
            {props.form.getFieldDecorator('freeText', {})(<Input allowClear placeholder='Enter' />)}
          </O2OFormItem>
        </Col>
        <Col xs={24} lg={12} xl={6} xxl={4}>
          <O2OFormItem label='Status'>
            {props.form.getFieldDecorator(
              'status',
              {}
            )(
              <Select allowClear mode={'multiple'} showArrow placeholder='Select(s)'>
                <Select.Option value={'Success'}>Success</Select.Option>
                <Select.Option value={'Failed'}>Failed</Select.Option>
              </Select>
            )}
          </O2OFormItem>
        </Col>
        <Col xs={24} lg={12} xl={7} xxl={8}>
          <O2OFormItem label='Time Range'>
            {props.form.getFieldDecorator(
              'time',
              {}
            )(
              <DatePicker.RangePicker
                allowClear
                style={{ width: '100%' }}
                showTime={{ defaultValue: [moment('00:00:00', 'HH:mm:ss'), moment('23:59:59', 'HH:mm:ss')] }}
              />
            )}
          </O2OFormItem>
        </Col>
        <Col>
          <O2OFormItem label=' '>
            <Button icon='search' loading={props.loading} onClick={onSubmit} type='primary'>
              Search
            </Button>
          </O2OFormItem>
        </Col>
      </Row>
    </Form>
  );
});
